/**
 * Fase 1a (escalabilidade) — jitter no re-sync disparado por um content change.
 *
 * Ideia: uma publicação de conteúdo emite `content_changed` em BROADCAST pra frota,
 * e todos sincronizam no mesmo instante → pico de rebuild no server (thundering
 * herd). Espalhar o re-sync num intervalo [0, jitterMax) achata o pico.
 *
 * Eventos TARGETED (o `device_id` do evento é ESTE player — ex.: o operador mexeu
 * só neste player) SEMPRE seguem imediatos (delay 0): é 1 player, não há herd, e
 * responsividade importa. Isso NÃO depende do jitter estar ligado ou não.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * DESLIGADO por padrão (decisão Renato 2026-08-19)
 * ──────────────────────────────────────────────────────────────────────────────
 * O jitter estava causando 2-3s de atraso perceptível na atualização de conteúdo/
 * campanha (o `config_update` é targeted → já era instantâneo; só o BROADCAST caía
 * no jitter). O herd test real (15k players, prod, cache ON) mostrou que a fila do
 * HikariCP enfileira o pico de forma graciosa: teto ~1000-1180 sync/s, p95 296ms,
 * **0 erros** — o server NÃO precisa do jitter pra sobreviver na escala atual.
 *
 * Então o teto EFETIVO agora é **0** (re-sync imediato). A LÓGICA continua no
 * código (não foi eliminada) — só o default virou 0.
 *
 * PARA RE-LIGAR o jitter numa build futura (SEM mudar código): exporte
 * `VITE_CONTENT_RESYNC_JITTER_MS=15000` (ou o valor desejado em ms) no ambiente do
 * build. O valor de referência de quando ligado é {@link CONTENT_RESYNC_JITTER_ENABLED_MS}.
 *
 * Helper puro/determinístico (rng injetável) — isolado do SyncService pra ser
 * testável sem o grafo de dependências do serviço.
 */

/**
 * Valor de referência do jitter QUANDO LIGADO (0–15s). Mantido no código: é o teto
 * que a Fase 1a usa pra espalhar o re-sync da frota. NÃO é o default hoje (vide
 * {@link CONTENT_RESYNC_JITTER_MS}) — serve de base pra re-ligar via env no build.
 */
export const CONTENT_RESYNC_JITTER_ENABLED_MS = 15 * 1000; // 0–15s (quando ligado)

/**
 * Teto EFETIVO do jitter. **DESLIGADO por padrão (0 = re-sync imediato).** Lê
 * `VITE_CONTENT_RESYNC_JITTER_MS` do build; ausente/ inválido → 0 (desligado).
 */
export const CONTENT_RESYNC_JITTER_MS = resolveJitterMaxMs();

function resolveJitterMaxMs(): number {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = (import.meta as any)?.env?.VITE_CONTENT_RESYNC_JITTER_MS;
  const n = raw != null && raw !== '' ? Number(raw) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : 0; // default: DESLIGADO
}

/**
 * Atraso (ms) do re-sync.
 * @param eventDeviceId device_id que veio no evento SSE (null = broadcast).
 * @param deviceId      id deste player.
 * @param jitterMaxMs   teto do jitter (default {@link CONTENT_RESYNC_JITTER_MS} — hoje 0).
 * @param rand          fonte de aleatoriedade [0,1) (injetável p/ teste).
 * @returns 0 se targeted a este player OU se o jitter está desligado (max 0);
 *          senão inteiro em [0, jitterMaxMs).
 */
export function computeContentResyncDelayMs(
  eventDeviceId: string | null,
  deviceId: string,
  jitterMaxMs: number = CONTENT_RESYNC_JITTER_MS,
  rand: () => number = Math.random,
): number {
  if (eventDeviceId === deviceId) {
    return 0;
  }
  if (jitterMaxMs <= 0) {
    return 0; // jitter desligado → re-sync imediato
  }
  return Math.floor(rand() * jitterMaxMs);
}
